import {
  hydrateMenuTagRefs,
  getMenuTagDefinition,
  resolveMenuItemLegalMarks,
} from '@web-studio/shared-schemas';
import { resolveLucideSsrByIconKey } from '../icons/lucideSsrIconNodes';
import { renderLucideIconHtml } from '../icons/renderIconHtml';
import {
  collectMenuLegalLegendDietary,
  collectMenuLegalLegendMarks,
} from '../lib/collectMenuLegalLegend';
import {
  buildMenuItemLegendTipGroups,
  formatMenuItemLegendTipPlain,
} from '../lib/menuItemLegendTip';
import type { MenuColumn, MenuItem } from '../lib/parseMenuColumns';
import { pickChrome } from '../lib/siteChromeCopy';
import { escapeHtml } from './escape';

export function renderMenuItemLegalSuperscriptHtml(item: MenuItem, locale: string): string {
  const marks = resolveMenuItemLegalMarks(item.allergenIds, item.additiveIds, locale);
  if (marks.length === 0) {
    return '';
  }
  const title = marks.map((mark) => `${mark.code} · ${mark.label}`).join(' · ');
  const text = marks.map((mark) => mark.code).join(',');
  return `<sup class="ws-menu__legal-sup" aria-label="${escapeHtml(title)}">${escapeHtml(text)}</sup>`;
}

function renderMenuItemLegendTipHtml(
  item: MenuItem,
  locale: string,
  includeLegalMarks: boolean,
): string {
  const groups = buildMenuItemLegendTipGroups(item, locale, { includeLegalMarks });
  if (groups.length === 0) {
    return '';
  }
  const body = groups
    .map((group) => {
      const rows = group.rows
        .map((row) => `<span class="ws-menu__item-legend-tip__row">${escapeHtml(row)}</span>`)
        .join('');
      return `<span class="ws-menu__item-legend-tip__group"><span class="ws-menu__item-legend-tip__title">${escapeHtml(group.title)}</span>${rows}</span>`;
    })
    .join('');
  return `<span class="ws-menu__item-legend-tip" role="tooltip">${body}</span>`;
}

export function renderMenuItemNameHtml(
  item: MenuItem,
  locale: string,
  options?: { nameClass?: string; codeClass?: string; showLegalMarks?: boolean; showOrderCode?: boolean },
): string {
  const nameClass = options?.nameClass ?? 'ws-menu__item-name';
  const codeClass = options?.codeClass ?? 'ws-menu__item-code';
  const showLegalMarks = options?.showLegalMarks !== false;
  const showOrderCode = options?.showOrderCode !== false;
  const code =
    showOrderCode && item.code?.trim()
      ? `<span class="${codeClass}">${escapeHtml(item.code.trim())}</span>`
      : '';
  const marks = showLegalMarks ? renderMenuItemLegalSuperscriptHtml(item, locale) : '';
  const legendGroups = buildMenuItemLegendTipGroups(item, locale, {
    includeLegalMarks: showLegalMarks,
  });
  const legendTip = renderMenuItemLegendTipHtml(item, locale, showLegalMarks);
  const tipClass = legendTip ? ' ws-menu__item-name--legend-tip' : '';
  const legendPlain = formatMenuItemLegendTipPlain(legendGroups);
  const tipAttrs = legendTip
    ? ` tabindex="0" aria-label="${escapeHtml(`${item.name}. ${legendPlain.replace(/\n/g, '. ')}`)}"`
    : '';
  return `<p class="${nameClass}${tipClass}"${tipAttrs}>${code}${escapeHtml(item.name)}${marks}${legendTip}</p>`;
}

/** Order-code column sibling (not inside the dish name). */
export function renderMenuItemOrderCodeHtml(
  code: string | null | undefined,
  className = 'ws-menu__item-code',
): string {
  const orderCode = code?.trim() ?? '';
  return `<span class="${className}"${orderCode ? '' : ' aria-hidden="true"'}>${escapeHtml(orderCode)}</span>`;
}

/** Price column slot — empty when missing so grid columns stay aligned. */
export function renderMenuItemPriceSlotHtml(
  price: string | null | undefined,
  className = 'ws-menu__item-price',
): string {
  const value = price?.trim() ?? '';
  return `<p class="${className}"${value ? '' : ' aria-hidden="true"'}>${escapeHtml(value)}</p>`;
}

/**
 * Speisekarte list row: code | copy (name + desc + dietary) | price.
 * Keeps wrapped name/description out from under the order code.
 */
export function renderMenuItemCodedRowHtml(options: {
  code: string | null | undefined;
  price: string | null | undefined;
  copyHtml: string;
  codeClass?: string;
  priceClass?: string;
  rowClass?: string;
  copyClass?: string;
}): string {
  const rowClass = options.rowClass ?? 'ws-menu__item-row';
  const copyClass = options.copyClass ?? 'ws-menu__item-copy';
  return `<div class="${rowClass}">${renderMenuItemOrderCodeHtml(options.code, options.codeClass)}<div class="${copyClass}">${options.copyHtml}</div>${renderMenuItemPriceSlotHtml(options.price, options.priceClass)}</div>`;
}

/** Dietary chips only — allergens/additives live in the name superscript. */
export function renderMenuItemDietaryTagsHtml(
  item: MenuItem,
  locale: string,
  options?: { iconsOnly?: boolean; placement?: 'stack' | 'inline' },
): string {
  const iconsOnly = options?.iconsOnly === true;
  const inline = options?.placement === 'inline';
  const dietary = hydrateMenuTagRefs(item.dietaryTagIds, locale).filter((tag) => {
    const def = getMenuTagDefinition(tag.id);
    if (def && def.kind !== 'dietary') {
      return false;
    }
    if (iconsOnly && !resolveLucideSsrByIconKey(tag.iconKey)) {
      return false;
    }
    return true;
  });
  if (dietary.length === 0) {
    return '';
  }
  const chips = dietary
    .map((tag) => {
      const ssrIcon = resolveLucideSsrByIconKey(tag.iconKey);
      const glyph = ssrIcon
        ? renderLucideIconHtml(ssrIcon.iconNode, {
            size: iconsOnly ? (inline ? 'sm' : 'md') : 'sm',
            strokeWidth: iconsOnly ? 2.25 : 1.75,
            name: ssrIcon.name,
            className: 'ws-menu__dietary-icon',
          })
        : '';
      if (iconsOnly && !glyph) {
        return '';
      }
      const label = iconsOnly
        ? ''
        : `<span class="ws-menu__dietary-label">${escapeHtml(tag.label)}</span>`;
      const tipClass = iconsOnly ? ' ws-menu__dietary-chip--tip' : '';
      const tipAttrs = iconsOnly
        ? ` data-label="${escapeHtml(tag.label)}" tabindex="0"`
        : ` title="${escapeHtml(tag.label)}"`;
      return `<span class="ws-menu__dietary-chip${tipClass}" role="listitem"${tipAttrs} aria-label="${escapeHtml(tag.label)}">${glyph}${label}</span>`;
    })
    .filter(Boolean)
    .join('');
  if (!chips) {
    return '';
  }
  const rootClass = [
    'ws-menu__dietary',
    iconsOnly ? 'ws-menu__dietary--icons' : '',
    inline ? 'ws-menu__dietary--inline' : '',
  ]
    .filter(Boolean)
    .join(' ');
  return `<div class="${rootClass}" role="list">${chips}</div>`;
}

function renderLegendGroupHtml(title: string, marks: { code: string; label: string; kind: string }[]): string {
  if (marks.length === 0) {
    return '';
  }
  const rows = marks
    .map(
      (mark) =>
        `<div class="ws-menu-legend__row"><dt class="ws-menu-legend__code">${escapeHtml(mark.code)}</dt><dd class="ws-menu-legend__label">${escapeHtml(mark.label)}</dd></div>`,
    )
    .join('');
  return `<div class="ws-menu-legend__group"><p class="ws-menu-legend__group-title">${escapeHtml(title)}</p><dl class="ws-menu-legend__list">${rows}</dl></div>`;
}

function renderDietaryLegendGroupHtml(
  title: string,
  entries: ReturnType<typeof collectMenuLegalLegendDietary>,
): string {
  if (entries.length === 0) {
    return '';
  }
  const rows = entries
    .map((entry) => {
      const ssrIcon = resolveLucideSsrByIconKey(entry.iconKey);
      const glyph = ssrIcon
        ? renderLucideIconHtml(ssrIcon.iconNode, {
            size: 'sm',
            strokeWidth: 2.25,
            name: ssrIcon.name,
            className: 'ws-menu-legend__dietary-icon',
          })
        : '<span aria-hidden="true">·</span>';
      return `<div class="ws-menu-legend__row"><dt class="ws-menu-legend__code ws-menu-legend__code--icon">${glyph}</dt><dd class="ws-menu-legend__label">${escapeHtml(entry.label)}</dd></div>`;
    })
    .join('');
  return `<div class="ws-menu-legend__group"><p class="ws-menu-legend__group-title">${escapeHtml(title)}</p><dl class="ws-menu-legend__list">${rows}</dl></div>`;
}

/** Collapsed-by-default Kennzeichnung footnote (no `open` on `<details>`). */
export function renderMenuLegalLegendHtml(
  columns: readonly MenuColumn[],
  locale: string,
): string {
  const marks = collectMenuLegalLegendMarks(columns, locale);
  const dietary = collectMenuLegalLegendDietary(columns, locale);
  if (marks.length === 0 && dietary.length === 0) {
    return '';
  }
  const allergens = marks.filter((mark) => mark.kind === 'allergen');
  const additives = marks.filter((mark) => mark.kind === 'additive');
  const summary = escapeHtml(pickChrome('menuLegalLegend', locale));
  const body = [
    renderLegendGroupHtml(pickChrome('menuLegalAllergens', locale), allergens),
    renderLegendGroupHtml(pickChrome('menuLegalAdditives', locale), additives),
    renderDietaryLegendGroupHtml(pickChrome('menuLegalDietary', locale), dietary),
  ].join('');

  return `<details class="ws-menu-legend"><summary class="ws-menu-legend__summary"><span class="ws-menu-legend__summary-label">${summary}</span></summary><div class="ws-menu-legend__body">${body}</div></details>`;
}
